<template>
  <div class="line-property-manager-panel">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item title="现有几何关系" name="existing-relations">
        <div class="existing-relations-section">
          <SelectionListFrame
              :items="existingRelations"
              selection-behavior="ctrl-multiple"
              list-role="list"
              :visible-rows="4"
              context-menu-enabled
              :is-item-delete-enabled="isSketchRelationSnapshotDeleteEnabled"
              :show-delete-all-action="existingRelations.some(isSketchRelationSnapshotDeleteEnabled)"
              item-key-field="relationId"
              @delete-item="handleDeleteRelationSelection"
              @delete-all="handleDeleteAllRelations"
          >
            <template #item="{ item }">
              <SketchRelationDiagnosticText
                :item="item"
                :label="resolveRelationName(item)"
              />
            </template>
          </SelectionListFrame>
        </div>
      </n-collapse-item>

      <n-collapse-item title="添加几何关系" name="add-relations">
        <div class="add-relations-section">
          <n-space size="small" wrap>
            <n-button
                v-for="relation in availableRelations"
                :key="relation.relationType"
                size="small"
                quaternary
                :disabled="relation.disabled"
                @click="handleAddRelation(relation.relationType)"
            >
              {{ relation.label }}
            </n-button>
          </n-space>
        </div>
      </n-collapse-item>

      <n-collapse-item title="选项" name="options">
        <div class="line-options-section">
          <n-checkbox
              :checked="lineInfo.isConstruction"
              @update:checked="handleConstructionChange"
          >
            作为构造线
          </n-checkbox>
          <n-checkbox
              :checked="lineInfo.isInfiniteLength"
              @update:checked="handleInfiniteChange"
          >
            无限长度
          </n-checkbox>
        </div>
      </n-collapse-item>

      <n-collapse-item title="参数" name="parameters">
        <div class="line-parameters-section">
          <div class="param-row">
            <n-text depth="3" class="param-label">长度</n-text>
            <n-input-number
                ref="lengthInputRef"
                :value="lengthDraft"
                size="small"
                :show-button="true"
                :precision="8"
                :step="1"
                @update:value="handleLengthDraftChange"
                @blur="commitLength('blur')"
                @keyup.enter="commitLength('enter')"
            />
          </div>
          <div class="param-row">
            <n-text depth="3" class="param-label">角度</n-text>
            <n-input-number
                :value="angleDraft"
                size="small"
                :show-button="true"
                :precision="8"
                :step="1"
                @update:value="handleAngleDraftChange"
                @blur="commitAngle('blur')"
                @keyup.enter="commitAngle('enter')"
            />
          </div>
        </div>
      </n-collapse-item>
      <n-collapse-item title="额外参数" name="extra">
        <n-text depth="3">当前无额外参数</n-text>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import {nextTick, onBeforeUnmount, onMounted, ref, shallowRef} from 'vue';
import {NButton, NCheckbox, NCollapse, NCollapseItem, NInputNumber, NSpace, NText,} from 'naive-ui';
import type {InputNumberInst} from 'naive-ui';
import SelectionListFrame from '../../SelectionListFrame.vue';
import SketchRelationDiagnosticText from '../common/SketchRelationDiagnosticText.vue';
import {isSketchRelationSnapshotDeleteEnabled} from '@/utils/sketch/isSketchRelationSnapshotDeleteEnabled';
import {
  AvailableRelationButtonViewModel,
  roundNullableToEightDecimalPlaces,
  roundToEightDecimalPlaces,
  SketchLineObject, SketchRelationKind,
  SketchRelationSnapshot,
  
  convertMmToM,
  convertMToMm,
  
} from '@puppycad/cad-core';
import {useGetSelectLine, useViewer} from '@/hooks';
import type {LineParametersSnapshot} from '@/hooks/selection/useGetSelectLine';
import type {Subscription} from 'rxjs';
import type {PropertyManagerContext} from '@/core';

interface LinePropertyManagerPanelProps {
  /** 属性面板上下文。 */
  context?: PropertyManagerContext;
}

const props = defineProps<LinePropertyManagerPanelProps>();

const {getSelectedLine, onLineChanged, onLineParametersChanged} = useGetSelectLine();
const { getViewer } = useViewer();

const selectedLine = shallowRef<SketchLineObject | null>(null);
/** 长度输入框实例。 */
const lengthInputRef = ref<InputNumberInst | null>(null);
let lineSubscription: Subscription | null = null;
/** 绘制中实时参数订阅。 */
let lineParamsSubscription: Subscription | null = null;
/** 等待当前落点 pointerup 的聚焦回调。 */
let pendingLengthFocusPointerUpHandler: (() => void) | null = null;
/** 延迟到鼠标 click 结束后执行的聚焦帧。 */
let pendingLengthFocusFrame: number | null = null;

/**
 * @description 默认展开分组。
 */
const defaultExpanded = [
  'existing-relations',
  'add-relations',
  'options',
  'parameters',
];

const availableRelations = ref<AvailableRelationButtonViewModel[]>([]);

const lineInfo = ref<LineParametersSnapshot>({
  isConstruction: false,
  isInfiniteLength: false,
  length: null,
  angleDeg: null,
});

const lengthDraft = ref<number | null>(null);
const angleDraft = ref<number | null>(null);

/**
 * 草稿是否来自用户亲手键入（而非选中/预览快照整体覆盖）。
 * 说明：
 * 1. 绘制连续线段时，移动鼠标会把"下一段预览长度/角度"整体刷进草稿用于实时显示；
 * 2. 若此刻输入框因失焦（如点击"取消"按钮）触发提交，会把预览值误写成上一段已提交线段的参数，
 *    表现为"第一段被拉长"；
 * 3. 因此只有用户键入过的草稿允许失焦提交，快照刷新的草稿失焦时仅回滚显示，不产生几何修改。
 */
let lengthDraftEditedByUser = false;
let angleDraftEditedByUser = false;

/**
 * @description 将统一线信息快照同步到面板状态与草稿值。
 * @param snapshot 线信息快照。
 */
function syncLineInfo(snapshot: LineParametersSnapshot): void {
  lineInfo.value.isConstruction = snapshot.isConstruction;
  lineInfo.value.isInfiniteLength = snapshot.isInfiniteLength;
  
  // 将底层的米（m）单位转换为毫米（mm）展示给用户
  lineInfo.value.length = snapshot.length !== null ? roundToEightDecimalPlaces(convertMToMm(snapshot.length)) : null;
  
  lineInfo.value.angleDeg = roundNullableToEightDecimalPlaces(snapshot.angleDeg);
  lengthDraft.value = lineInfo.value.length;
  angleDraft.value = lineInfo.value.angleDeg;
  // 快照驱动会整体覆盖草稿（选中切换/预览刷新），此时草稿不代表用户意图，清除键入标记。
  lengthDraftEditedByUser = false;
  angleDraftEditedByUser = false;
}

/**
 * @description 从线对象读取信息并同步到面板。
 * @param line 当前线对象。
 */
function syncLineInfoFromLine(line: SketchLineObject | null): void {
  if (!line) {
    syncLineInfo({
      isConstruction: false,
      isInfiniteLength: false,
      length: null,
      angleDeg: null,
    });
    return;
  }
  const length = line.getLength();
  const angleDeg = line.angleDeg;
  syncLineInfo({
    isConstruction: line.isConstruction,
    isInfiniteLength: line.isInfiniteLength,
    length: Number.isFinite(length) ? length : null,
    angleDeg: Number.isFinite(angleDeg) ? angleDeg : null,
  });
}

function refreshAvailableRelations(line: SketchLineObject | null): void {
  if (!line) {
    availableRelations.value = [];
    return;
  }
  availableRelations.value = getViewer()?.sketchRelationCreationService.build(line) ?? [];
}

function refreshPanelState(line: SketchLineObject | null): void {
  refreshExistingRelations(line);
  refreshAvailableRelations(line);
}

const existingRelations = ref<SketchRelationSnapshot[]>([]);

function refreshExistingRelations(line: SketchLineObject | null): void {
  if (!line) {
    existingRelations.value = [];
    return;
  }
  existingRelations.value = line.constraints.getRelationSnapshots()
}

/**
 * 将直线属性编辑交给统一参数系统与历史链路收口。
 * @param line 被编辑的直线。
 * @param lineLengthTarget 本次声明式输入的目标长度（米）。
 */
function finalizeLinePropertyEdit(line: SketchLineObject, lineLengthTarget?: number): void {
  props.context?.finalizeSketchPropertyEdit?.(line, {
    reason: 'property-edit',
    sourceObjectId: line.uuid,
    ...(lineLengthTarget !== undefined ? {lineLengthTarget} : {}),
    ...(lineLengthTarget !== undefined && props.context?.isDrawingMode === true
      ? {preserveLineStartPoint: true}
      : {}),
  });
}

/**
 * 取消尚未执行的长度输入聚焦请求。
 */
function cancelPendingLengthFocus(): void {
  if (pendingLengthFocusPointerUpHandler) {
    window.removeEventListener('pointerup', pendingLengthFocusPointerUpHandler, true);
    pendingLengthFocusPointerUpHandler = null;
  }
  if (pendingLengthFocusFrame !== null) {
    window.cancelAnimationFrame(pendingLengthFocusFrame);
    pendingLengthFocusFrame = null;
  }
}

/**
 * 在当前线段落点的完整鼠标流程结束后聚焦长度输入框。
 *
 * 设计说明：
 * 1. 线段创建与选中发生在 `mousedown` 中；
 * 2. 如果只等待 `nextTick` 就聚焦，后续 `pointerup/click` 仍可把焦点拿回视口；
 * 3. 因此先等待 `pointerup`，再在下一帧聚焦并全选，使用户可直接键入替换长度。
 */
async function requestLengthEditorFocus(): Promise<void> {
  await nextTick();
  cancelPendingLengthFocus();
  const handlePointerUp = (): void => {
    window.removeEventListener('pointerup', handlePointerUp, true);
    pendingLengthFocusPointerUpHandler = null;
    pendingLengthFocusFrame = window.requestAnimationFrame(() => {
      pendingLengthFocusFrame = null;
      lengthInputRef.value?.focus();
      lengthInputRef.value?.select();
    });
  };
  pendingLengthFocusPointerUpHandler = handlePointerUp;
  window.addEventListener('pointerup', handlePointerUp, {capture: true, once: true});
}

onMounted(() => {
  selectedLine.value = getSelectedLine();
  syncLineInfoFromLine(selectedLine.value);
  refreshPanelState(selectedLine.value);
  lineSubscription = onLineChanged().subscribe((line) => {
    const previousLine = selectedLine.value;
    selectedLine.value = line;
    syncLineInfoFromLine(line);
    refreshPanelState(line);
    if (props.context?.isDrawingMode === true && line && line !== previousLine) {
      // 仅新绘制线段进入属性态时请求聚焦，普通选择不抢占键盘。
      void requestLengthEditorFocus();
    }
  });

  
  lineParamsSubscription = onLineParametersChanged().subscribe((snapshot: LineParametersSnapshot) => {
    // 预览只同步当前鼠标线段参数，不触发输入框聚焦。
    syncLineInfo(snapshot);
  });
  
});

onBeforeUnmount(() => {
  cancelPendingLengthFocus();
  if (lineSubscription) {
    lineSubscription.unsubscribe();
  }
  if (lineParamsSubscription) {
    lineParamsSubscription.unsubscribe();
  }
});


function resolveRelationName(item: unknown): string {
  if (!item || typeof item !== 'object') {
    return '';
  }
  const relation = item as SketchRelationSnapshot;
  return `${relation.name ?? ''}${relation.isSuppressed ? '（抑制）' : ''}`;
}

async function removeLineRelation(line: SketchLineObject, relationId: number): Promise<boolean> {
  return await props.context?.removeSketchRelation?.(line, relationId) ?? false;
}

async function handleDeleteRelationSelection(payload: { item: unknown; index: number }): Promise<void> {
  const line = selectedLine.value;
  if (!line) {
    return;
  }
  const relation = payload.item as SketchRelationSnapshot;
  if (!relation || !Number.isFinite(relation.id) || !isSketchRelationSnapshotDeleteEnabled(relation)) {
    return;
  }
  if (!await removeLineRelation(line, relation.id)) {
    return;
  }
  // 同步移除场景中的几何关系标签
  const viewer = getViewer();
  viewer?.removeObjectConstraintMarker?.(line, relation.id);
  refreshPanelState(line);
}

function handleAddRelation(type: SketchRelationKind): void {
  const line = selectedLine.value;
  const viewer = getViewer();

  if (!line || !viewer) {
    return;
  }
  const created = viewer.sketchRelationCreationService.createRelation({
    objects:[line],
    relationKind: type,
  })
  if (!created) {
    return;
  }
  refreshPanelState(line);
}

async function handleDeleteAllRelations(): Promise<void> {
  const line = selectedLine.value;
  if (!line) {
    return;
  }
  const viewer = getViewer();
  for (const item of existingRelations.value.filter(isSketchRelationSnapshotDeleteEnabled)) {
    if (!await removeLineRelation(line, item.id)) {
      return;
    }
    // 同步移除场景中的几何关系标签
    viewer?.removeObjectConstraintMarker?.(line, item.id);
  }
  refreshPanelState(line);
}

function handleConstructionChange(value: boolean): void {
  const line = selectedLine.value;
  if (!line) {
    return;
  }
  // 直接通过对象 setter 修改构造线状态（setter 内部切换材质）。
  line.isConstruction = value;
  const viewer = getViewer();
  viewer?.syncObjectBaseVisualState?.(line);
  if (line.closedCollection) {
    if (value || !viewer?.shadedSketchContoursEnabled) {
      line.closedCollection.clearContourDisplay?.();
    } else {
      line.closedCollection.syncContour?.();
    }
  }
  finalizeLinePropertyEdit(line);
  syncLineInfoFromLine(line);
  refreshPanelState(line);
}

function handleInfiniteChange(value: boolean): void {
  const line = selectedLine.value;
  if (!line) {
    return;
  }
  // 直接通过对象 setter 修改无限长度状态（setter 内部调用 updateGeometry）。
  line.isInfiniteLength = value;
  finalizeLinePropertyEdit(line);
  syncLineInfoFromLine(line);
  refreshPanelState(line);
}

function handleLengthDraftChange(value: number | null): void {
  lengthDraft.value = roundNullableToEightDecimalPlaces(value);
  // 记录"值来自用户键入"，允许失焦提交；快照刷新会再次覆盖并清除该标记。
  lengthDraftEditedByUser = true;
}

function handleAngleDraftChange(value: number | null): void {
  angleDraft.value = roundNullableToEightDecimalPlaces(value);
  // 记录"值来自用户键入"，允许失焦提交；快照刷新会再次覆盖并清除该标记。
  angleDraftEditedByUser = true;
}

/**
 * 提交长度草稿。
 * 说明：
 * 1. 失焦提交守卫：仅用户亲手键入过的草稿可提交；纯快照（选中/预览）刷新的草稿在失焦时
 *    只回滚显示，避免"点击取消按钮导致输入框 blur"把下一段预览长度误写成已提交线段。
 * 2. Enter/显式调用视为用户确认，保留提交语义。
 * @param trigger 提交触发来源。
 */
function commitLength(trigger: 'enter' | 'blur' = 'enter'): void {
  const line = selectedLine.value;
  if (!line) {
    return;
  }
  if (trigger === 'blur' && !lengthDraftEditedByUser) {
    // 失焦但草稿未被用户键入：仅恢复正式值显示，不产生属性修改。
    syncLineInfoFromLine(line);
    return;
  }
  if (lengthDraft.value === null || !Number.isFinite(lengthDraft.value)) {
    lengthDraft.value = lineInfo.value.length;
    lengthDraftEditedByUser = false;
    return;
  }
  try {
    
    // 长度是约束图中的派生量，不能先单独移动某一端点；把米制目标直接交给统一求解器。
    const targetLengthInM = roundToEightDecimalPlaces(convertMmToM(lengthDraft.value));
    props.context?.prepareSketchPropertyEdit?.(line);
    finalizeLinePropertyEdit(line, targetLengthInM);
    syncLineInfoFromLine(line);
    refreshPanelState(line);
  } catch {
    lengthDraft.value = lineInfo.value.length;
  }
  lengthDraftEditedByUser = false;
}

/**
 * 提交角度草稿。
 * 说明：与 commitLength 相同，失焦时只提交用户键入过的值，防止预览角度被误写回已提交线段。
 * @param trigger 提交触发来源。
 */
function commitAngle(trigger: 'enter' | 'blur' = 'enter'): void {
  const line = selectedLine.value;
  if (!line) {
    return;
  }
  if (trigger === 'blur' && !angleDraftEditedByUser) {
    // 失焦但草稿未被用户键入：仅恢复正式值显示，不产生属性修改。
    syncLineInfoFromLine(line);
    return;
  }
  if (angleDraft.value === null || !Number.isFinite(angleDraft.value)) {
    angleDraft.value = lineInfo.value.angleDeg;
    angleDraftEditedByUser = false;
    return;
  }
  try {
    line.angleDeg = roundToEightDecimalPlaces(angleDraft.value);
    finalizeLinePropertyEdit(line);
    syncLineInfoFromLine(line);
    refreshPanelState(line);
  } catch {
    angleDraft.value = lineInfo.value.angleDeg;
  }
  angleDraftEditedByUser = false;
}
</script>

<style scoped>
.line-property-manager-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.existing-relations-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.relation-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
}


.add-relations-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.line-options-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.line-parameters-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-row {
  display: grid;
  grid-template-columns: 48px 1fr;
  gap: 8px;
  align-items: center;
}

.param-label {
  font-size: 12px;
}
</style>
